// =====================================================
// PARTICIPANT REGISTRATION DIALOG
// Stage 2: Participants & Pricing
// Dialog for registering ministers or guests to an event
// =====================================================

import { useState } from 'react';
import { UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MinisterSearchCombobox } from './MinisterSearchCombobox';
import { GuestRegistrationForm, type GuestFormData } from './GuestRegistrationForm';
import { PricePreviewCard } from './PricePreviewCard';
import { validateGuestForm } from '../utils/guestValidation';
import { useParticipantMutations } from '../hooks/useEventParticipants';
import * as eventGuestsRepo from '@/data/eventGuestsRepository';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';

// ===================
// TYPES
// ===================

interface ParticipantRegistrationDialogProps {
  eventId: string;
  basePrice: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type RegistrationTab = 'minister' | 'guest';

// ===================
// COMPONENT
// ===================

export function ParticipantRegistrationDialog({
  eventId,
  basePrice,
  open,
  onOpenChange,
}: ParticipantRegistrationDialogProps) {
  const [activeTab, setActiveTab] = useState<RegistrationTab>('minister');
  const [selectedMinisterId, setSelectedMinisterId] = useState<string | undefined>();
  const [guestData, setGuestData] = useState<GuestFormData>({
    full_name: '',
    dni: '',
    email: '',
    phone: '',
    age: undefined,
    church_affiliation: '',
  });
  const [guestErrors, setGuestErrors] = useState<Partial<Record<keyof GuestFormData, string>>>({});
  // Fast entry at the desk: keep the dialog open after each registration
  const [keepOpen, setKeepOpen] = useState(false);

  const resetForNext = () => {
    setSelectedMinisterId(undefined);
    setGuestData({ full_name: '', dni: '', email: '', phone: '', age: undefined, church_affiliation: '' });
    setGuestErrors({});
  };

  const { registerParticipant, isRegistering } = useParticipantMutations(eventId);
  const { toast } = useToast();

  // Reset form when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setActiveTab('minister');
      setSelectedMinisterId(undefined);
      setGuestData({
        full_name: '',
        dni: '',
        email: '',
        phone: '',
        age: undefined,
        church_affiliation: '',
        representing_minister_id: undefined,
      });
      setGuestErrors({});
    }
    onOpenChange(newOpen);
  };

  // Handle minister registration
  const handleRegisterMinister = async () => {
    if (!selectedMinisterId) {
      return;
    }

    registerParticipant(
      {
        event_id: eventId,
        minister_id: selectedMinisterId,
        calculate_price: true,
      },
      {
        onSuccess: () => {
          if (keepOpen) resetForNext();
          else handleOpenChange(false);
        },
      }
    );
  };

  // Handle guest registration
  const handleRegisterGuest = async () => {
    // Validate form
    const validation = validateGuestForm(guestData);
    if (!validation.valid) {
      setGuestErrors(validation.errors);
      return;
    }

    try {
      // Reuse an existing guest with the same CI, else create one
      const guest = await eventGuestsRepo.findOrCreateEventGuest({
        full_name: guestData.full_name,
        dni: guestData.dni,
        email: guestData.email,
        phone: guestData.phone,
        age: guestData.age,
        church_affiliation: guestData.church_affiliation,
        representing_minister_id: guestData.representing_minister_id,
      });

      // Then register as participant
      registerParticipant(
        {
          event_id: eventId,
          guest_id: guest.id,
          calculate_price: true,
        },
        {
          onSuccess: () => {
            if (keepOpen) resetForNext();
            else handleOpenChange(false);
          },
        }
      );
    } catch (error) {
      console.error('Error creating guest:', error);
      toast({
        title: 'Error al registrar invitado',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    }
  };

  const canRegister = activeTab === 'minister' ? !!selectedMinisterId : guestData.full_name.length >= 3;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar participante</DialogTitle>
          <DialogDescription>
            Selecciona un ministro o registra un invitado para este evento
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val as RegistrationTab)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="minister" className="gap-2">
              <UserPlus className="size-4" />
              Ministro
            </TabsTrigger>
            <TabsTrigger value="guest" className="gap-2">
              <Users className="size-4" />
              Invitado
            </TabsTrigger>
          </TabsList>

          {/* Minister Registration Tab */}
          <TabsContent value="minister" className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Seleccionar Ministro</label>
              <MinisterSearchCombobox
                eventId={eventId}
                value={selectedMinisterId}
                onSelect={setSelectedMinisterId}
                disabled={isRegistering}
              />
            </div>

            {selectedMinisterId && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Precio Calculado</label>
                <PricePreviewCard
                  eventId={eventId}
                  ministerId={selectedMinisterId}
                  basePrice={basePrice}
                />
              </div>
            )}

            {!selectedMinisterId && (
              <Alert>
                <AlertDescription>
                  Busca y selecciona un ministro para ver el precio calculado
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          {/* Guest Registration Tab */}
          <TabsContent value="guest" className="space-y-4">
            <GuestRegistrationForm
              eventId={eventId}
              value={guestData}
              onChange={setGuestData}
              errors={guestErrors}
            />

            {guestData.full_name.length >= 3 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Precio Calculado</label>
                <PricePreviewCard
                  eventId={eventId}
                  asGuest
                  basePrice={basePrice}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox checked={keepOpen} onCheckedChange={(c) => setKeepOpen(c === true)} />
            Registrar otro después
          </label>
          <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isRegistering}>
            Cancelar
          </Button>
          <Button
            onClick={activeTab === 'minister' ? handleRegisterMinister : handleRegisterGuest}
            disabled={!canRegister || isRegistering}
          >
            {isRegistering ? 'Registrando...' : `Registrar participante`}
          </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
